<template>
    <!-- Back to a filtered URL reloads with preserved state, so a category switch must remount to pick up its own store. -->
    <CategoryPageLayout :key="categoryPage.$id" :overrides="overrides">
        <template #head>
            <Head>
                <link
                    v-if="lcpPreloadAttributes"
                    rel="preload"
                    as="image"
                    fetchpriority="high"
                    head-key="category-lcp"
                    v-bind="lcpPreloadAttributes"
                />

                <component v-if="faqPageJsonLd" :is="'script'" type="application/ld+json" head-key="category-faqpage-jsonld">{{
                    faqPageJsonLd
                }}</component>
                <component v-if="itemListJsonLd" :is="'script'" type="application/ld+json" head-key="category-itemlist-jsonld">{{
                    itemListJsonLd
                }}</component>
            </Head>
        </template>

        <div>
            <CategorySearch :initial-results="initialResults" :lister-page-description="listerPageDescription" />
        </div>
    </CategoryPageLayout>
</template>

<script setup lang="ts">
import CategoryPageLayout from '@/components/layouts/CategoryPageLayout.vue';
import { categoryProductImageSizes } from '@/composables/category/useCategorySearch';
import { buildCategoryItemListJsonLd } from '@/composables/seo/useCategoryItemListJsonLd';
import { buildCategoryFaqPageJsonLd } from '@/composables/seo/useFaqPageJsonLd';
import { serializeJsonLd } from '@/composables/seo/useJsonLd';
import CategorySearch from '@/pages/category/partials/CategorySearch.vue';
import { categoryPageStore } from '@/stores/category/CategoryPageStore';
import type { AppPageProps } from '@/types';
import type { CategoryInitialResultObject } from '@/types/generated';
import type { SeoOverrides } from '@/types/seo';
import { buildTypedSrcset } from '@/utils/imageUrl';
import { Head, usePage } from '@inertiajs/vue3';
import _ from 'lodash';
import { computed } from 'vue';

type CategoryPageProps = {
    initial_results?: CategoryInitialResultObject[];
};

const props = defineProps<CategoryPageProps>();

const page = usePage<AppPageProps>();
const categoryPage = computed(() => categoryPageStore());

const initialResults = computed((): CategoryInitialResultObject[] => props.initial_results ?? []);

const listerPageDescription = computed((): string | undefined => categoryPage.value.categoryListerPage?.description ?? undefined);

const overrides = computed((): SeoOverrides => ({
    title: categoryPage.value.title,
    description: categoryPage.value.metaDescription ?? null,
}));

// Must match the first product card's <img> exactly (src, srcset, sizes) or the browser
// downloads the image twice: once for the preload, once for the mismatched real request.
// Head renders an unset attribute as the string "undefined", so only defined attributes are bound.
const lcpPreloadAttributes = computed((): Record<string, string> | undefined => {
    const imageUrl = initialResults.value[0]?.image_url ?? undefined;

    if (!imageUrl) {
        return undefined;
    }

    const imageSrcset = initialResults.value[0]?.image_srcset ?? buildTypedSrcset(imageUrl);

    return _.omitBy({ href: imageUrl, imagesrcset: imageSrcset, imagesizes: categoryProductImageSizes }, _.isNil) as Record<string, string>;
});

const itemListJsonLd = computed((): string | null =>
    serializeJsonLd(
        buildCategoryItemListJsonLd(
            categoryPage.value.title,
            initialResults.value,
            page.props.store?.magento_store_code,
            page.props.store?.clean_root_url,
        ),
    ),
);
const faqPageJsonLd = computed((): string | null => serializeJsonLd(buildCategoryFaqPageJsonLd(categoryPage.value.category?.faqs ?? [])));
</script>
